import { ActivatedRoute, Router } from '@angular/router';
import {
  Component,
  effect,
  inject,
  resource,
  signal,
} from '@angular/core';
import { DatePipe, KeyValuePipe } from '@angular/common';
import { environment } from '@environment';
import { FormControlAutocompleteComponent } from '@components/form-control/autocomplete/autocomplete.component';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormControlMarkdownComponent } from '@components/form-control/markdown/markdown.component';
import { HttpParams, HttpResourceRef, httpResource } from '@angular/common/http';
import { HumanReadableSizePipe } from '@pipes';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { MapArchiveContributorsFormComponent } from '@components/map/archive/form/contributors/contributors.component';
import { MapArchiveOrgFormComponent } from '@components/map/archive/form/organisations/organisations.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatListModule,
  MatListOption,
} from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MapScaleValidator } from '@validators';

import {
  AuthService,
  AttachmentService,
  MapArchiveService,
  NotificationService,
} from '@services';

import {
  Attachment,
  AttachmentListResponse,
  Contributor,
  CountryList,
  MapArchiveEntry,
  MapEntryDetails,
  MapLicense,
  MapOrg,
  MapOrgRole,
  MapType,
  Permission,
  PublicationType,
  WorldLanguage,
} from '@models';
import { PromptComponent } from '@components/prompt/prompt.component';
import { DatasetFormAttachmentComponent } from '@components/dataset/form/attachment/attachment.component';
import { DatasetFormAttachmentCreateComponent } from '@components/dataset/form/attachment/create/create.component';
import { DatasetFormAttachmentDeleteComponent } from '@components/dataset/form/attachment/delete/delete.component';

@Component({
  selector: 'app-map-archive-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  imports: [
    DatePipe,
    FormControlAutocompleteComponent,
    FormControlMarkdownComponent,
    HumanReadableSizePipe,
    KeyValuePipe,
    LeafletGroupComponent,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatPaginatorModule,
    MatSelectModule,
    MatStepperModule,
    ReactiveFormsModule,
  ],
})
export class MapArchiveStepperComponent {
  // Enums
  countryList = CountryList;
  mapTypes = MapType;
  mapLicense = MapLicense;
  worldLangs = WorldLanguage;
  publicationType = PublicationType;
  mapOrgRole = MapOrgRole;

  // Route
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Services
  authService = inject(AuthService);
  mapArchiveService = inject(MapArchiveService);
  notificationService = inject(NotificationService);
  attachmentService = inject(AttachmentService);

  // Map Data
  mapEntry = signal<MapArchiveEntry>(this.route.snapshot.data['map']);
  permissions = signal<Permission | null | undefined>(
    this.route.snapshot.data['permissions'],
  );

  // Validator settings
  reqValid = { validators: [Validators.required] };

  // General Form

  preamble: FormControl = new FormControl(this.mapEntry().content.preamble, {});
  title: FormControl = new FormControl(
    this.mapEntry().content.title,
    this.reqValid,
  );
  subtitle: FormControl = new FormControl(this.mapEntry().content.subtitle, {});
  entryType: FormControl = new FormControl(
    this.mapEntry().content.entryType,
    this.reqValid,
  );
  serie: FormControl = new FormControl(this.mapEntry().content.serie, {});
  summary: FormControl = new FormControl(
    this.mapEntry().content.summary,
    this.reqValid,
  );
  scales: FormControl = new FormControl(
    (this.mapEntry().content.scales || []).join(', '),
    { validators: [MapScaleValidator] },
  );
  mapType: FormControl = new FormControl(this.mapEntry().content.mapType, {});

  generalForm: FormGroup = new FormGroup({
    preamble: this.preamble,
    title: this.title,
    subtitle: this.subtitle,
    entryType: this.entryType,
    serie: this.serie,
    summary: this.summary,
    scales: this.scales,
    mapType: this.mapType,
  });

  // Form for ScanDetails

  scanDetails: Record<string, FormGroup> = {};
  scanDetailsYear: Record<string, FormControl> = {};
  scanDetailsCountry: Record<string, FormControl> = {};
  scanDetailsPlace: Record<string, FormControl> = {};
  scanDetailsLanguages: Record<string, FormControl> = {};
  scanDetailsCode: Record<string, FormControl> = {};
  scanDetailsLicense: Record<string, FormControl> = {};

  // Autocomplete setup

  // Country autocomplete
  countries: string[] = Object.values(this.countryList).map((country: string) => country);
  countrySearch = signal('');
  countryResource = resource({
    params: () => ({ q: this.countrySearch() }),
    loader: async (p) => {
      return this.countries.filter((country: string) => {
        return country.toLowerCase().includes(p.params.q.toLocaleLowerCase());
      });
    }
  });

  // Language autocomplete
  langs: string[] = Object.values(this.worldLangs).map((lang: string) => lang);
  langSearch = signal('');
  langResource = resource({
    params: () => ({ q: this.langSearch() }),
    loader: async (p) => {
      return this.langs.filter((lang: string) => {
        return lang.toLowerCase().includes(p.params.q.toLocaleLowerCase());
      });
    }
  });

  // Image attachments

  defaultPageSize = 50;

  sourceCount = signal(0);
  sourcePage = signal(0);
  sourcePageSize = signal(this.defaultPageSize);
  sourceList = httpResource<AttachmentListResponse>(() => {
    const httpParams = new HttpParams()
      .set('skip', this.sourcePage() * this.sourcePageSize())
      .set('take', this.sourcePageSize())
      .set('count', true)
      .set('prefix', '/source/')
      .set('recordId', this.mapEntry().id)
      .set('order', 'created:asc');

    return `${environment.kinkoHost}/dataset/${environment.mapArchiveDatasetId}/attachment/?${httpParams.toString()}`;
  });

  sourcePageChange(event: PageEvent) {
    this.sourcePage.set(event.pageIndex);
    this.sourcePageSize.set(event.pageSize);
  }

  sourceUpload(event: Event) {
    const element = event.target as HTMLInputElement;
    const files: FileList | null = element.files;

    if (!files) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentCreateComponent, {
      data: {
        datasetId: this.mapEntry().datasetId,
        files: files,
        recordId: this.mapEntry().id,
        prefix: '/source/',
      },
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe(() => {
      element.value = '';
      this.sourceList.reload();
    });
  }

  // Retrieve a full page of alternate images. This list is used to cleanup
  // alternate formats when a source image is deleted from the archive.
  //
  // @NOTE!!! If for some reason a map entry has more than 127 scans attached
  // to it then this list will be incomplete. This means that when a scan is
  // deleted that has processed images outside the page range they won't get
  // cleaned up.
  altList = httpResource<AttachmentListResponse>(() => {
    const httpParams = new HttpParams()
      .set('take', 255)
      .set('count', true)
      .set('prefix', '/processed/')
      .set('recursive', true)
      .set('recordId', this.mapEntry().id)
      .set('order', 'created:asc');

    return `${environment.kinkoHost}/dataset/${environment.mapArchiveDatasetId}/attachment/?${httpParams.toString()}`;
  });

  attachmentEdit(list: HttpResourceRef<AttachmentListResponse | undefined>, items: MatListOption[]) {
    const ids = items.map((o) => o.value as string);
    const attachments = list.value()?.items
      .filter((a) => ids.includes(a.id));

    if (!attachments?.length) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentComponent, {
      data: attachments,
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe((updatedAttachments: Attachment[]) => {
      if (!updatedAttachments) {
        return;
      }
      list.reload();
    });
  }

  attachmentDelete(list: HttpResourceRef<AttachmentListResponse | undefined>, items: MatListOption[]) {
    const ids = items.map((o) => o.value as string);
    let attachments = list.value()?.items
      .filter((a) => ids.includes(a.id));

    if (!attachments?.length) {
      return;
    }

    // Lookup related processing results and flag them for delete
    const altItems = this.altList.value()?.items
      .filter((a) => ids.includes(a.parentId));

    if (altItems?.length) {
      attachments = altItems.concat(attachments);
    };

    this.dialog.open(DatasetFormAttachmentDeleteComponent, {
      data: attachments,
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe((deletedAttachments: Attachment[]) => {
      if (!deletedAttachments?.length) {
        return;
      }
      list.reload();
    });
  }

  // Stepper settings
  isLinear = signal<boolean>(false);

  private dialog = inject(MatDialog);

  constructor() {
    // Effects

    // Get the amount of images found for the archive entry.
    effect(() => {
      this.sourceCount.set(this.sourceList.value()?.count || 0);
    });

    // Update stepper UI state for each uploaded source image. We want to
    // add a step to the form that allows the user to provide details for
    // that specific map image.

    const entry = this.mapEntry();

    effect(() => {
      const list = this.sourceList.value()?.items;
      if (!list?.length) {
        return;
      }

      if (list.length === 1) {
        this.entryType.setValue('single', {});
      }

      const currentPublications = entry.content.scans || {};
      entry.content.scans = {}; // Clear the publication object
      for (const img of list) {
        if (currentPublications[img.id]) {
          // If info is already set retain it.
          entry.content.scans[img.id] = currentPublications[img.id];
        } else {
          // Load default publication info
          entry.content.scans[img.id] = {
            license: this.mapLicense['copyright'],
          };
        }

        const viewCountry = entry.content.scans[img.id]?.publicationCountry || null;
        const viewLangs = entry.content.scans[img.id]?.languages || null;

        // For each image initialize the FormControls
        this.scanDetailsCode[img.id] = new FormControl(
          entry.content.scans[img.id].code,
          {},
        );
        this.scanDetailsCountry[img.id] = new FormControl(
          viewCountry,
          {},
        );
        this.scanDetailsLanguages[img.id] = new FormControl(
          viewLangs,
          {},
        );
        this.scanDetailsLicense[img.id] = new FormControl(
          entry.content.scans[img.id].license,
          {},
        );
        this.scanDetailsPlace[img.id] = new FormControl(
          entry.content.scans[img.id].publicationPlace,
          {},
        );
        this.scanDetailsYear[img.id] = new FormControl(
          entry.content.scans[img.id].publicationYear,
          {},
        );

        // Initialize the FormGroup for each image

        this.scanDetails[img.id] = new FormGroup({
          code: this.scanDetailsCode[img.id],
          country: this.scanDetailsCountry[img.id],
          languages: this.scanDetailsLanguages[img.id],
          license: this.scanDetailsLicense[img.id],
          place: this.scanDetailsPlace[img.id],
          year: this.scanDetailsYear[img.id],
        });
      }
    });
  } // !!! CONSTRUCTOR END !!!

  // Update the map signal with the provided entry
  updateMapArchive(entry: MapArchiveEntry) {
    this.mapArchiveService.updateMap(this.mapEntry().id, entry).subscribe({
      next: () => {
        this.mapEntry.set(entry);
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  onGeoChange(geometry: GeoJSON.FeatureCollection) {
    const entry = this.mapEntry();

    if (!geometry?.features.length) {
      delete entry.content.geojson;
    } else {
      entry.content.geojson = geometry;
    }

    this.updateMapArchive(entry);
  }

  contributorForm(contrib?: Contributor) {
    this.dialog.open(MapArchiveContributorsFormComponent, {
      data: contrib,
      minWidth: '360px',
      width: '480px',
    }).afterClosed().subscribe((updatedContrib: Contributor) => {
      if (!updatedContrib) {
        return;
      }

      const entry = this.mapEntry();
      entry.content.contributors = [...(entry.content.contributors || [])];

      if (contrib) {
        const index = entry.content.contributors.indexOf(contrib);
        if (index !== -1) {
          entry.content.contributors[index] = updatedContrib;
        }
      } else {
        entry.content.contributors.push(updatedContrib);
      }

      this.updateMapArchive(entry);
    });
  }

  contributorDelete(items: MatListOption[]) {
    const entry = this.mapEntry();
    entry.content.contributors = (entry.content?.contributors || [])
      .filter(o => items.findIndex(opt => opt.value === o) === -1);
    this.updateMapArchive(entry);
  }

  orgForm(org?: MapOrg) {
    this.dialog.open(MapArchiveOrgFormComponent, {
      data: org,
      minWidth: '360px',
      width: '480px',
    }).afterClosed().subscribe((updatedOrg: MapOrg) => {
      if (!updatedOrg) {
        return;
      }

      const entry = this.mapEntry();
      entry.content.organisations = [...(entry.content.organisations || [])];

      if (org) {
        const index = entry.content.organisations.indexOf(org);
        if (index !== -1) {
          entry.content.organisations[index] = updatedOrg;
        }
      } else {
        entry.content.organisations.push(updatedOrg);
      }

      this.updateMapArchive(entry);
    });
  }

  orgDelete(items: MatListOption[]) {
    const entry = this.mapEntry();
    entry.content.organisations = (entry.content?.organisations || [])
      .filter(o => items.findIndex(opt => opt.value === o) === -1);
    this.updateMapArchive(entry);
  }

  onClose() {
    // Check if general form is pristine
    let pristine = this.generalForm.pristine;

    // Check if map detail forms are pristine
    const list = this.sourceList.value()?.items;
    if (list?.length) {
      for (const img of list) {
        pristine = pristine && this.scanDetails[img.id].pristine;
      }
    }

    // If one of the forms isn't pristine we show the confirm dialog else we
    // navigate back to the map overview.
    if (!pristine) {
      this.dialog
        .open(PromptComponent, {
          minWidth: '360px',
          width: '480px',
          data: {
            title: 'Unsaved data',
            content: 'If you continue these changes will be discarded.',
          },
        })
        .afterClosed()
        .subscribe((result: boolean) => {
          if (result) {
            this.router.navigate(['/map/archive/', this.mapEntry().id]);
          }
        });
    } else {
      this.router.navigate(['/map/archive/', this.mapEntry().id]);
    }
  }

  submit() {
    if (!this.generalForm.pristine && this.generalForm.valid) {
      let scales = [];
      if (this.generalForm.value.scales !== '') {
        scales = this.generalForm.value.scales.split(',').map((s: string) => {
          return s.trim();
        });
      }

      const generalInfo = {
        preamble: this.generalForm.value.preamble?.trim(),
        title: this.generalForm.value.title?.trim(),
        subtitle: this.generalForm.value.subtitle?.trim(),
        entryType: this.generalForm.value.entryType,
        serie: this.generalForm.value.serie?.trim(),
        summary: this.generalForm.value.summary,
        scales: scales,
        mapType: this.generalForm.value.mapType,
      } as MapEntryDetails;

      const entry = this.mapEntry();
      entry.content = { ...entry.content, ...generalInfo };
      this.mapArchiveService.updateMap(this.mapEntry().id, entry).subscribe({
        next: () => {
          this.mapEntry.set(entry);

          // After update mark the formGroup as pristine
          this.generalForm.markAsPristine();
          this.notificationService.info('Sucessfully saved changes.');
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        },
      });
    }

    const list = this.sourceList.value()?.items;

    if (list?.length) {
      for (const img of list) {
        if (
          !this.scanDetails[img.id].pristine &&
          this.scanDetails[img.id].valid
        ) {
          const entry = this.mapEntry();
          if (!entry.content.scans) {
            entry.content.scans = {};
          }

          entry.content.scans[img.id] = {
            code: this.scanDetails[img.id].value.code,
            languages: this.scanDetails[img.id].value.languages,
            license: this.scanDetails[img.id].value.license,
            publicationCountry: this.scanDetails[img.id].value.country,
            publicationPlace: this.scanDetails[img.id].value.place,
            publicationYear: this.scanDetails[img.id].value.year,
          };

          this.mapArchiveService.updateMap(this.mapEntry().id, entry).subscribe({
            next: () => {
              this.mapEntry.set(entry);

              // After update mark the formGroup as pristine
              this.scanDetails[img.id].markAsPristine();
              this.notificationService.info('Sucessfully saved changes.');
            },
            error: (err) => {
              console.error(err);
              this.notificationService.error('Failed to save changes.');
            },
          });
        }
      }
    }
  }
}
